/**
 * adminHash.ts — T-649 top-level admin view discriminator for the router-less OptiYield SPA
 * (pure, no DOM).
 *
 * The app has no router (Ricardo's ruling). T-237 established that top-level state rides in the URL
 * hash; this adds the ONE dimension T-649 needs — which authed VIEW is showing: the map, or the
 * admin page (and which section of it). The admin hash is `#admin` or `#admin/<section>`.
 *
 * It cannot collide with a map hash by construction: `parseMapHash` (urlHash.ts) requires a numeric
 * zoom/lat/lng triplet as its first '/'-segment, while every admin hash's first segment is the
 * literal `admin`. So `parseMapHash('#admin…')` is null and `parseAdminHash('#4/39/-98…')` is null —
 * the two channels are disjoint. That disjointness is what lets a single hash carry both the
 * top-level view discriminator and (under the map view) the camera without a router; it is asserted
 * in both directions in adminHash.test.ts.
 */

export const ADMIN_SECTIONS = ['model', 'users', 'operator', 'history'] as const
export type AdminSection = (typeof ADMIN_SECTIONS)[number]
export const DEFAULT_ADMIN_SECTION: AdminSection = 'model'

/** Serialise the admin view to the URL hash (leading '#' included). Bare `#admin` = default section. */
export function encodeAdminHash(section: AdminSection = DEFAULT_ADMIN_SECTION): string {
  return section === DEFAULT_ADMIN_SECTION ? '#admin' : `#admin/${section}`
}

/**
 * Parse a hash into the admin view, or null if it is not an admin hash (a map hash, empty, or
 * garbage). A bare `#admin` resolves to the default section; an unknown section (`#admin/xyz`, a
 * stale link) falls back to the default rather than null, so an out-of-date admin link still lands
 * on the admin page instead of silently bouncing to the map.
 */
export function parseAdminHash(hash: string): { section: AdminSection } | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  // The admin view carries no &-params today; drop any defensively before reading the path.
  const firstSegment = raw.split('&')[0] ?? ''
  const parts = firstSegment.split('/')
  if (parts[0] !== 'admin') return null
  const candidate = parts[1]
  const section = ADMIN_SECTIONS.includes(candidate as AdminSection)
    ? (candidate as AdminSection)
    : DEFAULT_ADMIN_SECTION
  return { section }
}
